import { ArrowPathIcon, CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { useSpinDelay } from 'spin-delay'
import { cn } from '#app/utils/misc.tsx'
import { Button } from './catalyst/button.tsx'

type ButtonProps = Extract<
	React.ComponentPropsWithoutRef<typeof Button>,
	{ href?: never }
>

// A plain `Omit` on a union type collapses to `Pick<T, Exclude<keyof T, K>>`,
// and `keyof` a union only yields keys common to every member — so it would
// flatten `ButtonProps`'s `color` / `outline` / `plain` union into one shape
// where all three are simultaneously optional. Distribute the `Omit` over
// each union member instead, to keep them mutually exclusive.
type DistributiveOmit<T, K extends PropertyKey> = T extends any
	? Omit<T, K>
	: never

export type StatusButtonProps = DistributiveOmit<ButtonProps, 'children'> & {
	children: React.ReactNode
	status: 'pending' | 'success' | 'error' | 'idle'
	message?: string | null
	spinDelay?: Parameters<typeof useSpinDelay>[1]
}

export function StatusButton({
	message,
	status,
	className,
	children,
	spinDelay,
	...props
}: StatusButtonProps) {
	const delayedPending = useSpinDelay(status === 'pending', {
		delay: 400,
		minDuration: 300,
		...spinDelay,
	})

	// Each status is a live region, so it needs text content of its own — the
	// icons are decorative and the `title` attribute is not announced reliably.
	const companion = {
		pending: delayedPending ? (
			<span
				role="status"
				title={message ?? 'loading'}
				className="inline-flex size-5 items-center justify-center"
			>
				<ArrowPathIcon className="size-4 animate-spin" aria-hidden />
				<span className="sr-only">{message ?? 'loading'}</span>
			</span>
		) : null,
		success: (
			<span
				role="status"
				title={message ?? 'success'}
				className="inline-flex size-5 items-center justify-center"
			>
				<CheckIcon className="size-4" aria-hidden />
				<span className="sr-only">{message ?? 'success'}</span>
			</span>
		),
		error: (
			<span
				role="status"
				title={message ?? 'error'}
				className="inline-flex size-5 items-center justify-center rounded-full bg-red-600 text-white"
			>
				<XMarkIcon className="size-3.5" aria-hidden />
				<span className="sr-only">{message ?? 'error'}</span>
			</span>
		),
		idle: null,
	}[status]

	return (
		<Button {...(props as ButtonProps)} className={cn('gap-2', className)}>
			<span>{children}</span>
			{companion}
		</Button>
	)
}
